import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  VStack,
} from '@chakra-ui/react';
import { useState, useCallback, useRef } from 'react';
import ReactToastify from '../../hooks/useReactToastify';
import { MdFileDownload, MdCloudUpload } from 'react-icons/md';
import { FiX } from 'react-icons/fi';
import ExcelJS from 'exceljs';
import { useUploadLeaveDataMutation } from '../../features/apis/authApi';
interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const LeaveUpload: React.FC<Props> = ({ isOpen, setIsOpen }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLeaveData] = useUploadLeaveDataMutation();
  const abortController = useRef<AbortController | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const simulateUpload = () => {
    setUploadProgress(0);
    setSubmitting(true);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setSubmitting(false);
          return 100;
        }
        return prev + 10;
      });
    }, 150);
  };
  const handleClose = () => {
    if (uploading) abortController.current?.abort();
    setFile(null);
    setUploading(false);
    setIsOpen(false);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    if (selectedFile) {
      simulateUpload();
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (
      droppedFile &&
      (droppedFile.name.endsWith('.csv') || droppedFile.name.endsWith('.xlsx'))
    ) {
      setFile(droppedFile);
    }
  }, []);

  const handleUpload = async () => {
    if (!file) return;

    try {
      setUploading(true);

      // ===================== LOAD FILE =====================
      const buffer = await file.arrayBuffer();

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(buffer);

      const sheet = workbook.worksheets[0];

      if (!sheet) {
        throw new Error('Worksheet not found');
      }

      // ===================== HEADER NORMALIZATION =====================
      const normalize = (value: any) =>
        String(value ?? '')
          .replace(/\uFEFF/g, '')
          .trim()
          .toLowerCase()
          .replace(/\s+/g, '');

      const headerRow = sheet.getRow(1);
      const headerMap: Record<string, number> = {};

      headerRow.eachCell((cell, colNumber) => {
        const key = normalize(cell.value);
        headerMap[key] = colNumber;
      });

      const getCol = (key: string) => headerMap[normalize(key)] ?? null;

      // ===================== REQUIRED COLUMN CHECK =====================
      const emailCol = getCol('email');
      if (!emailCol) {
        console.log('HEADERS FOUND:', Object.keys(headerMap));
        throw new Error('Email column not found in Excel file');
      }

      // ===================== SAFE NUMBER PARSER =====================
      const getNum = (row: any, key: string) => {
        const col = getCol(key);
        if (!col) return 0;

        const value = row.getCell(col).value;

        if (value === null || value === undefined || value === '') return 0;

        const num = Number(value);
        return isNaN(num) ? 0 : num;
      };

      // ===================== ROW PARSING =====================
      const rows: any[] = [];

      sheet.eachRow((row: any, rowNumber: number) => {
        if (rowNumber === 1) return;

        const email = row.getCell(emailCol).value;

        if (!email || String(email).trim() === '') return;

        rows.push({
          email: String(email).trim().toLowerCase(),

          general: getNum(row, 'general'),
          optional: getNum(row, 'optional'),
          bereavement: getNum(row, 'bereavement'),
          paternity: getNum(row, 'paternity'),
          maternity: getNum(row, 'maternity'),
          marriage: getNum(row, 'marriage'),
          anniversary: getNum(row, 'anniversary'),
          birthday: getNum(row, 'birthday'),
          sick: getNum(row, 'sick'),
          other: getNum(row, 'other'),
        });
      });

      // ===================== VALIDATION =====================
      if (rows.length === 0) {
        throw new Error('No valid rows found in file');
      }

      // ===================== API CALL =====================
      await uploadLeaveData(rows).unwrap();

      setUploadProgress(0);

      ReactToastify({
        success: true,
        message: 'Leave data uploaded successfully!',
      });

      handleClose();
    } catch (err) {
      console.error(err);

      ReactToastify({
        success: false,
        message: (err as Error).message || 'Upload failed',
      });
    } finally {
      setUploading(false);
    }
  };

  const cancelUpload = () => {
    abortController.current?.abort();
    setUploading(false);
    setUploadProgress(0);
  };
  const handleDownloadSample = async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Sample Leave Upload');

    sheet.columns = [
      { header: 'Email', key: 'email', width: 30 },
      { header: 'General', key: 'general', width: 12 },
      { header: 'Optional', key: 'optional', width: 12 },
      { header: 'Bereavement', key: 'bereavement', width: 15 },
      { header: 'Paternity', key: 'paternity', width: 12 },
      { header: 'Maternity', key: 'maternity', width: 12 },
      { header: 'Marriage', key: 'marriage', width: 12 },
      { header: 'Anniversary', key: 'anniversary', width: 12 },
      { header: 'Birthday', key: 'birthday', width: 12 },
      { header: 'Sick', key: 'sick', width: 12 },
    ];
    sheet.addRow({
      email: 'john@example.com',
      general: 5,
      optional: 2,
      bereavement: 0,
      paternity: 0,
      maternity: 0,
      marriage: 0,
      anniversary: 0,
      birthday: 1,
      sick: 3,
    });
    sheet.getRow(1).font = { bold: true };

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leave-upload-sample.xlsx';
    a.click();
    window.URL.revokeObjectURL(url);
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size={{ base: 'full', md: 'xl', lg: '2xl' }}
    >
      <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />{' '}
      <ModalContent>
        <ModalHeader margin={'0'}>Import Leave Data</ModalHeader>
        <ModalCloseButton isDisabled={uploading} />

        <ModalBody className="space-y-4" marginTop={0}>
          <Button
            onClick={handleDownloadSample} // ✅ connect function here
            color="blue.500"
            fontWeight="medium"
            display="flex"
            alignItems="center"
            gap={2}
            fontSize="sm"
            cursor="pointer"
          >
            <MdFileDownload size={14} />
            Download Sample Sheet
          </Button>

          <Box
            border="2px dashed"
            borderColor={isDragging ? 'blue.300' : 'gray.200'}
            bg={isDragging ? 'blue.50' : 'gray.50'}
            borderRadius="lg"
            p={{ base: 6, md: 10 }}
            textAlign="center"
            cursor={uploading ? 'not-allowed' : 'pointer'}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onClick={() =>
              !uploading && document.getElementById('file-input')?.click()
            }
            transition="all 0.2s"
            opacity={uploading ? 0.6 : 1}
          >
            <VStack spacing={3}>
              <MdCloudUpload size={40} color="#805AD5" />{' '}
              {/* slightly smaller icon */}
              <Text fontSize="md" fontWeight="medium">
                Drag and Drop or{' '}
                <Text as="span" color="blue.500" fontSize="md">
                  Choose File
                </Text>{' '}
                to Upload
              </Text>
              <Text fontSize="xs" color="gray.500">
                CSV or XLSX file formats supported
              </Text>
            </VStack>
          </Box>

          <input
            id="file-input"
            type="file"
            accept=".csv,.xlsx"
            onChange={handleChange}
            style={{ display: 'none' }}
          />

          {file && (
            <div className="mb-4 w-full bg-[#F3F3F3] p-4 rounded-md">
              <div>
                <span className="text-base ">{file.name}</span>
                <p className="text-[#A7A7A7] my-">
                  size: {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
              <div className="w-full h-1 bg-gray-200 rounded">
                <div
                  className="h-full bg-[#B277E5] rounded transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                ></div>
              </div>
            </div>
          )}
        </ModalBody>
        <ModalFooter
          justifyContent="flex-end"
          gap={3}
          flexDirection={{ base: 'column', md: 'row' }}
          width="100%"
        >
          {!uploading || submitting ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleClose}
                rightIcon={<FiX size={13} />}
                color="gray.500"
                borderWidth="0.5px"
                borderColor="#D4D4D4"
                _hover={{ bg: 'gray.100', color: 'gray.700', borderColor: '#D4D4D4' }}
                borderRadius="lg"
                width={{ base: '100%', md: 'auto' }}
              >
                Cancel Upload
              </Button>

              <Button
                borderRadius="6px"
                bgGradient="linear(to-r, #6535EB, #9F3198)"
                color="white"
                height={{ base: '26px', md: '34px' }}
                px={{ base: 3, md: 5 }}
                fontSize={{ base: '10px', md: '12px' }}
                fontWeight="500"
                whiteSpace="nowrap"
                transition="all 0.2s ease"
                _hover={{
                  transform: 'translateY(-1px)',
                  bgGradient: 'linear(to-r, #5a2fe0, #8a2a85)',
                }}
                _active={{
                  transform: 'scale(0.98)',
                }}
                onClick={handleUpload}
                isDisabled={!file}
                rightIcon={<span>→</span>}
                width={{ base: '100%', md: 'auto' }}
              >
                Upload
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={cancelUpload}
              rightIcon={<FiX size={13} />}
              color="gray.500"
              borderWidth="0.5px"
              borderColor="#D4D4D4"
              _hover={{ bg: 'gray.100', color: 'gray.700', borderColor: '#D4D4D4' }}
              borderRadius="lg"
              width={{ base: '100%', md: 'auto' }}
            >
              Cancel Upload
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LeaveUpload;
